import React from 'react';
import Image from '@/components/atoms/Image';
import Button from '@/components/atoms/button';
import QuantityWidget from '@/components/atoms/quantity-widget';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { CurrencyHelpers } from '@/utils/currency-helpers';
import { ProductItemProps } from '../../../types';

const ProductItem = ({ product, quantity, handleQuantityChange, onRemove }: ProductItemProps) => {
  const translate = useTranslations();
  const lineTotal = product.price ? CurrencyHelpers.multiplyCurrency(product.price, quantity) : undefined;

  return (
    <div className="flex items-start gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-white">
        {product.image && (
          <Image
            src={product.image}
            alt={product.name}
            width={64}
            height={64}
            className="h-full w-full object-contain p-1"
          />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-13 line-clamp-2 leading-tight font-semibold text-gray-700">{product.name}</p>
            <p className="text-12 mt-0.5 text-gray-500">
              {translate('common.cat-no')}: {product.sku}
            </p>
          </div>
          <Button
            variant="ghost"
            size="s"
            Icon={TrashIcon}
            onClick={() => onRemove(product)}
            aria-label={translate('quick-order.remove-item', { name: product.name })}
            className="shrink-0"
          />
        </div>

        <div className="mt-2 flex items-center justify-between">
          <QuantityWidget
            value={quantity}
            minValue={1}
            maxValue={product.maxQuantity}
            showLabel={false}
            onChange={(value) => handleQuantityChange(product, value)}
          />
          {product.price && (
            <div className="text-right">
              <p className="text-14 text-primary font-bold">
                {lineTotal
                  ? CurrencyHelpers.formatForCurrency(lineTotal)
                  : CurrencyHelpers.formatForCurrency(product.price)}
              </p>
              {quantity > 1 && (
                <p className="text-12 text-gray-500">{CurrencyHelpers.formatForCurrency(product.price)} each</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductItem;
